import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2, MapPin, FolderTree } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RegionsTab({ language }) {
  const isLV = language === 'lv';
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingSlug, setEditingSlug] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [fileInput, setFileInput] = useState(null);
  const [busy, setBusy] = useState(false);
  // OSM locations import state
  const [importStatus, setImportStatus] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [hierStatus, setHierStatus] = useState(null);
  const [hierBusy, setHierBusy] = useState(false);
  // Map category marker colours (admin-editable).
  const MAP_COLOR_CATS = [
    { key: 'shop', lv: 'Veikali', en: 'Shops' },
    { key: 'service', lv: 'Pakalpojumi', en: 'Services' },
    { key: 'event', lv: 'Pasākumi', en: 'Events' },
    { key: 'project', lv: 'Projekti', en: 'Projects' },
    { key: 'business', lv: 'Uzņēmumi', en: 'Businesses' },
  ];
  const [mapColors, setMapColors] = useState(null);
  const [mapColorDefaults, setMapColorDefaults] = useState({});
  const [colorsBusy, setColorsBusy] = useState(false);

  const loadMapColors = async () => {
    try {
      const { data } = await axios.get(`${API}/map/colors`);
      setMapColors(data.colors || {});
      setMapColorDefaults(data.defaults || {});
    } catch (e) { /* ignore */ }
  };

  const saveMapColors = async () => {
    setColorsBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/map-colors`, mapColors);
      if (data.colors) setMapColors((c) => ({ ...c, ...data.colors }));
      toast.success(isLV ? 'Kartes krāsas saglabātas' : 'Map colours saved');
    } catch (e) {
      toast.error(formatApiError(e, isLV ? 'Saglabāšana neizdevās' : 'Save failed'));
    } finally { setColorsBusy(false); }
  };

  const loadHierStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/locations/import-hierarchy/status`);
      setHierStatus(data);
    } catch (e) { /* endpoint may not be deployed yet */ }
  };

  const triggerHierImport = async () => {
    if (!window.confirm(isLV
      ? 'Importēt administratīvo hierarhiju (novadi + pagasti) no OpenStreetMap un piesaistīt katru vietu tās pagastam/novadam? Process ilgst ~2-3 minūtes.'
      : 'Import the administrative hierarchy (municipalities + parishes) from OpenStreetMap and assign every place to its parish/municipality? Takes ~2-3 minutes.')) return;
    setHierBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/locations/import-hierarchy`);
      if (data.status === 'already_running') {
        toast.info(isLV ? 'Hierarhijas imports jau notiek...' : 'Hierarchy import already running...');
      } else {
        toast.success(isLV ? 'Hierarhijas imports sākts (~2-3 min).' : 'Hierarchy import started (~2-3 min).');
      }
      let polls = 0;
      const pollInterval = setInterval(async () => {
        polls += 1;
        await loadHierStatus();
        if (polls >= 48 || (hierStatus && !hierStatus.running && hierStatus.summary)) {
          clearInterval(pollInterval);
          setHierBusy(false);
        }
      }, 5000);
    } catch (e) {
      toast.error(formatApiError(e, isLV ? 'Hierarhijas imports neizdevās' : 'Hierarchy import failed'));
      setHierBusy(false);
    }
  };

  const loadImportStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/locations/import-osm/status`);
      setImportStatus(data);
    } catch (e) { /* ignore — endpoint may not be deployed yet */ }
  };

  const triggerImport = async () => {
    if (!window.confirm(isLV
      ? 'Importēt visas Latvijas pilsētas, ciemus un pagastus no OpenStreetMap? Process ilgst ~30-60 sekundes.'
      : 'Import all Latvian cities, villages and parishes from OpenStreetMap? Takes ~30-60 seconds.')) return;
    setImportBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/locations/import-osm`);
      if (data.status === 'already_running') {
        toast.info(isLV ? 'Importēšana jau notiek...' : 'Import already running...');
      } else {
        toast.success(isLV ? 'Importēšana sākta. Pārbaudi statusu pēc 30-60 sekundēm.' : 'Import started. Check status in 30-60 seconds.');
      }
      // Poll status every 5 seconds for up to 3 minutes
      let polls = 0;
      const pollInterval = setInterval(async () => {
        polls += 1;
        await loadImportStatus();
        if (polls >= 36 || (importStatus && !importStatus.running && importStatus.summary)) {
          clearInterval(pollInterval);
          setImportBusy(false);
        }
      }, 5000);
    } catch (e) {
      toast.error(formatApiError(e, isLV ? 'Importēšana neizdevās' : 'Import failed'));
      setImportBusy(false);
    }
  };

  const loadRegions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/regions`);
      setRegions(data.regions || []);
    } catch (e) { console.error('Region load failed', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRegions(); loadImportStatus(); loadHierStatus(); loadMapColors(); }, []);

  const filtered = regions.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.slug.includes(search.toLowerCase())
  );

  const saveOverride = async (slug) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (fileInput) fd.append('file', fileInput);
      else if (urlInput) fd.append('url', urlInput);
      else return;
      await axios.post(`${API}/admin/regions/${slug}/coat-of-arms`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadRegions();
      setEditingSlug(null);
      setUrlInput('');
      setFileInput(null);
    } catch (e) {
      alert(formatApiError(e, 'Save failed'));
    } finally { setBusy(false); }
  };

  const clearOverride = async (slug) => {
    if (!window.confirm(isLV ? 'Atjaunot Wikipedia ģerboni?' : 'Reset to Wikipedia coat of arms?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/regions/${slug}/coat-of-arms`);
      await loadRegions();
    } catch (e) {
      alert(formatApiError(e, 'Reset failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-regions-tab">
      {/* Map category marker colours — used by the composite region markers and
          the category chips below the map. Distinct from geography layers. */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="admin-map-colors">
        <h2 className="font-outfit text-lg font-bold text-[#121212]">
          {isLV ? 'Kartes krāsas' : 'Map colours'}
        </h2>
        <p className="text-sm text-[#5C5C5C] mt-1 max-w-3xl">
          {isLV
            ? 'Katras kategorijas burbuļa krāsa kartē (reģionu marķieros un kategoriju čipos). Krāsām jāatšķiras no ģeogrāfijas slāņiem (Pilsētas, Mikrorajoni, Ciemi).'
            : 'The colour of each category bubble on the map (region markers + category chips). Keep them distinct from the geography layers.'}
        </p>
        {mapColors ? (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {MAP_COLOR_CATS.map((c) => (
                <div key={c.key} className="border border-[#E5E4E0] rounded-xl p-3" data-testid={`map-color-${c.key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-4 h-4 rounded-full border border-white shadow" style={{ background: mapColors[c.key] }} />
                    <span className="text-sm font-semibold text-[#121212]">{isLV ? c.lv : c.en}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={mapColors[c.key] || '#000000'}
                      onChange={(e) => setMapColors((m) => ({ ...m, [c.key]: e.target.value }))}
                      className="w-9 h-9 rounded cursor-pointer border border-[#E5E4E0] bg-white p-0.5"
                      data-testid={`map-color-picker-${c.key}`}
                    />
                    <Input
                      type="text"
                      value={mapColors[c.key] || ''}
                      onChange={(e) => setMapColors((m) => ({ ...m, [c.key]: e.target.value }))}
                      className="h-9 text-xs font-mono flex-1"
                      data-testid={`map-color-hex-${c.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                onClick={saveMapColors}
                disabled={colorsBusy}
                className="bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="map-colors-save-btn"
              >
                {colorsBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isLV ? 'Saglabā...' : 'Saving...'}</> : (isLV ? 'Saglabāt krāsas' : 'Save colours')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setMapColors({ ...mapColorDefaults })}
                disabled={colorsBusy}
                data-testid="map-colors-reset-btn"
              >
                {isLV ? 'Atjaunot noklusējumu' : 'Reset to defaults'}
              </Button>
            </div>
          </>
        ) : (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#D97736]" /></div>
        )}
      </div>

      {/* OSM locations import — used to populate the empty `locations` collection
          after a fresh deployment (e.g. on production where the DB starts empty). */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="font-outfit text-lg font-bold text-[#121212] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D97736]" />
              {isLV ? 'Latvijas lokāciju importēšana (OSM)' : 'Latvian locations import (OSM)'}
            </h2>
            <p className="text-sm text-[#5C5C5C] mt-1 max-w-3xl">
              {isLV
                ? 'Lejupielādē visas Latvijas pilsētas, ciemus, rajonus un pagastus no OpenStreetMap. Nepieciešams pēc pirmās izvietošanas produkcijā vai jaunā vidē. Process ilgst ~30-60 sekundes.'
                : 'Downloads all Latvian cities, villages, districts and parishes from OpenStreetMap. Required after a fresh deployment. Takes ~30-60 seconds.'}
            </p>
            {importStatus && (
              <div className="mt-3 flex gap-4 flex-wrap text-sm">
                <div>
                  <span className="text-[#5C5C5C]">{isLV ? 'Šobrīd datu bāzē' : 'Currently in DB'}:</span>{' '}
                  <span className="font-bold text-[#121212]" data-testid="locations-total-count">
                    {importStatus.total_locations}
                  </span>
                </div>
                {importStatus.by_type && Object.entries(importStatus.by_type).map(([t, n]) => (
                  <div key={t}>
                    <span className="text-[#5C5C5C]">{t}:</span>{' '}
                    <span className="font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            )}
            {importStatus && importStatus.running && (
              <p className="mt-3 text-sm text-amber-700 flex items-center gap-2" data-testid="import-running">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isLV ? 'Importēšana notiek...' : 'Import in progress...'}
                {importStatus.started_at && (
                  <span className="text-xs text-[#5C5C5C]">
                    ({isLV ? 'sākts' : 'started'} {new Date(importStatus.started_at).toLocaleTimeString()})
                  </span>
                )}
              </p>
            )}
            {importStatus && importStatus.summary && !importStatus.running && (
              <p className="mt-3 text-sm text-green-700" data-testid="import-summary">
                ✓ {isLV ? 'Pēdējā importēšana' : 'Last import'}: {importStatus.summary.inserted} {isLV ? 'jauni' : 'new'}, {importStatus.summary.updated} {isLV ? 'atjaunoti' : 'updated'}, {importStatus.summary.skipped} {isLV ? 'izlaisti' : 'skipped'}
              </p>
            )}
            {importStatus && importStatus.error && (
              <p className="mt-3 text-sm text-red-700">
                ✗ {isLV ? 'Kļūda' : 'Error'}: {importStatus.error}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={triggerImport}
              disabled={importBusy || (importStatus && importStatus.running)}
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid="import-osm-btn"
            >
              {importBusy || (importStatus && importStatus.running) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isLV ? 'Importē...' : 'Importing...'}</>
              ) : (
                <>{isLV ? 'Importēt no OSM' : 'Import from OSM'}</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadImportStatus}
              data-testid="import-refresh-status-btn"
            >
              {isLV ? 'Atsvaidzināt statusu' : 'Refresh status'}
            </Button>
          </div>
        </div>
      </div>

      {/* Administrative hierarchy import — links every place to its parish + municipality */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="font-outfit text-lg font-bold text-[#121212] flex items-center gap-2">
              <FolderTree className="w-5 h-5 text-emerald-600" />
              {isLV ? 'Administratīvā hierarhija (novadi + pagasti)' : 'Administrative hierarchy (municipalities + parishes)'}
            </h2>
            <p className="text-sm text-[#5C5C5C] mt-1 max-w-3xl">
              {isLV
                ? 'Importē administratīvās robežas no OpenStreetMap un piesaista katru ciemu/pilsētu tās pagastam un novadam (ciems → pagasts → novads → reģions). Palaist pēc lokāciju importa. Ilgst ~2-3 minūtes.'
                : 'Imports OSM admin boundaries and assigns every village/town to its parish and municipality (village → parish → municipality → region). Run after the OSM locations import. Takes ~2-3 minutes.'}
            </p>
            {hierStatus && hierStatus.by_type && (
              <div className="mt-3 flex gap-4 flex-wrap text-sm">
                {['parish', 'municipality'].map((t) => (
                  <div key={t}>
                    <span className="text-[#5C5C5C]">{t}:</span>{' '}
                    <span className="font-semibold" data-testid={`hier-count-${t}`}>{hierStatus.by_type[t] || 0}</span>
                  </div>
                ))}
              </div>
            )}
            {hierStatus && hierStatus.running && (
              <p className="mt-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="hier-running">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isLV ? 'Hierarhijas imports notiek...' : 'Hierarchy import in progress...'}
              </p>
            )}
            {hierStatus && hierStatus.summary && !hierStatus.running && (
              <p className="mt-3 text-sm text-green-700" data-testid="hier-summary">
                ✓ {hierStatus.summary.parishes} {isLV ? 'pagasti' : 'parishes'}, {hierStatus.summary.municipalities} {isLV ? 'novadi' : 'municipalities'}, {hierStatus.summary.places_assigned} {isLV ? 'vietas piesaistītas' : 'places assigned'}
              </p>
            )}
            {hierStatus && hierStatus.error && (
              <p className="mt-3 text-sm text-red-700">✗ {isLV ? 'Kļūda' : 'Error'}: {hierStatus.error}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={triggerHierImport}
              disabled={hierBusy || (hierStatus && hierStatus.running)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="import-hierarchy-btn"
            >
              {hierBusy || (hierStatus && hierStatus.running) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isLV ? 'Importē...' : 'Importing...'}</>
              ) : (
                <>{isLV ? 'Importēt hierarhiju' : 'Import hierarchy'}</>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={loadHierStatus} data-testid="hier-refresh-status-btn">
              {isLV ? 'Atsvaidzināt statusu' : 'Refresh status'}
            </Button>
          </div>
        </div>
      </div>

    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-outfit text-xl font-bold text-[#121212]">
            {isLV ? 'Reģionu pārvaldība' : 'Region management'}
          </h2>
          <p className="text-sm text-[#5C5C5C] mt-1">
            {isLV
              ? 'Pārraksti pilsētas ģerboni ar augšupielādētu attēlu vai URL. Pēc dzēšanas — atjaunojas no Wikipedia.'
              : 'Override a city coat of arms with an upload or URL. Delete to fall back to Wikipedia.'}
          </p>
        </div>
        <Input
          type="text"
          placeholder={isLV ? 'Meklēt pilsētu...' : 'Search city...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
          data-testid="region-search-input"
        />
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#D97736]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((r) => (
            <div
              key={r.slug}
              className="border border-[#E5E4E0] rounded-xl p-3 flex flex-col"
              data-testid={`region-card-${r.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                  {r.coat_of_arms_url ? (
                    <img
                      src={r.coat_of_arms_url}
                      alt={r.name}
                      className="max-w-full max-h-full object-contain p-1"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <MapPin className="w-6 h-6 text-[#5C5C5C]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#121212] truncate">{r.name}</div>
                  <div className="text-xs text-[#5C5C5C]">
                    {r.listings_count} {isLV ? 'sludinājumi' : 'listings'}
                  </div>
                  <div className="text-xs">
                    {r.source === 'admin' ? (
                      <span className="text-emerald-700">{isLV ? 'Manuāls' : 'Manual'}</span>
                    ) : r.source === 'wikipedia' ? (
                      <span className="text-blue-600">Wikipedia</span>
                    ) : (
                      <span className="text-[#5C5C5C]">{isLV ? 'Nav iestatīts' : 'Not set'}</span>
                    )}
                  </div>
                </div>
              </div>

              {editingSlug === r.slug ? (
                <div className="mt-2 space-y-2">
                  <Input
                    type="text"
                    placeholder={isLV ? 'Attēla URL' : 'Image URL'}
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="h-8 text-xs"
                    data-testid={`region-url-input-${r.slug}`}
                  />
                  <div className="text-xs text-[#5C5C5C]">{isLV ? 'vai' : 'or'}</div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={(e) => setFileInput(e.target.files[0] || null)}
                    className="text-xs"
                    data-testid={`region-file-input-${r.slug}`}
                  />
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      onClick={() => saveOverride(r.slug)}
                      disabled={busy || (!urlInput && !fileInput)}
                      className="h-7 px-2 text-xs bg-[#D97736] hover:bg-[#C06528] text-white"
                      data-testid={`region-save-${r.slug}`}
                    >{isLV ? 'Saglabāt' : 'Save'}</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setEditingSlug(null); setUrlInput(''); setFileInput(null); }}
                      className="h-7 px-2 text-xs"
                    >{isLV ? 'Atcelt' : 'Cancel'}</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex gap-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingSlug(r.slug); setUrlInput(r.coat_of_arms_url || ''); }}
                    className="h-7 px-2 text-xs"
                    data-testid={`region-edit-${r.slug}`}
                  >{isLV ? 'Mainīt' : 'Edit'}</Button>
                  {r.source === 'admin' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => clearOverride(r.slug)}
                      className="h-7 px-2 text-xs text-red-700 border-red-200 hover:bg-red-50"
                      data-testid={`region-clear-${r.slug}`}
                    >{isLV ? 'Atiestatīt' : 'Reset'}</Button>
                  )}
                  <a
                    href={`/region/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="h-7 px-2 text-xs flex items-center text-[#5C5C5C] hover:text-[#D97736]"
                  >
                    /region/{r.slug} ↗
                  </a>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-sm text-[#5C5C5C] py-6">
              {isLV ? 'Nav rezultātu' : 'No results'}
            </div>
          )}
        </div>
      )}
    </div>
    </div>
  );
}

export default RegionsTab;
